import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { Section, SectionHead, Chips, RarityDot } from "@/components/landing/Section";
import { TrollArt } from "@/components/landing/TrollArt";
import JournalDialog from "@/components/landing/JournalDialog";
import { FILTERS, RARITY_COLORS, TROLLS, isPainted } from "@/lib/trolls";

const KEYS = ["all", "common", "rare", "epic", "legendary", "festivals", "merchants", "weekend", "promo"];
const PAGE = 12;
const ORDERED = [...TROLLS.filter(isPainted), ...TROLLS.filter((t) => !isPainted(t))];

const TrollCard = ({ troll, onOpen, i }) => {
  const { t, tl } = useLang();
  const color = RARITY_COLORS[troll.rarity];
  return (
    <motion.button
      type="button"
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: (i % PAGE) * 0.03 }}
      onClick={() => onOpen(troll)}
      data-testid={`troll-card-${troll.slug}`}
      className="group journal-card p-4 text-left flex flex-col items-center text-center hover:-translate-y-1 transition-[transform,box-shadow] duration-200 hover:shadow-journal-lg"
    >
      <div
        className="relative w-full h-48 md:h-56 rounded-t-[999px] rounded-b-[14px] flex items-end justify-center overflow-hidden"
        style={{ background: `linear-gradient(to top, ${color}38, ${color}0a 70%, transparent)` }}
      >
        <TrollArt troll={troll} className="h-[94%] w-auto max-w-[92%] transition-transform duration-300 origin-bottom group-hover:scale-[1.04]" />
        {!isPainted(troll) && (
          <span className="absolute top-4 left-1/2 -translate-x-1/2 rounded-full bg-parchment/90 border border-line px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.14em] text-ink-muted" data-testid={`troll-card-hiding-${troll.slug}`}>
            {t.gallery.hiding}
          </span>
        )}
      </div>
      <div className="font-display text-lg font-semibold text-ink mt-3 leading-tight">{troll.name}</div>
      <div className="text-sm font-bold text-rowan mt-0.5 leading-snug">{tl(troll, "epithet")}</div>
      <div className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-ink-muted">
        <RarityDot color={color} /> {t.rarity[troll.rarity]}
      </div>
    </motion.button>
  );
};

export default function TrollGallery() {
  const { t } = useLang();
  const [filter, setFilter] = useState("all");
  const [limit, setLimit] = useState(PAGE);
  const [open, setOpen] = useState(null);
  const list = useMemo(() => ORDERED.filter(FILTERS[filter]), [filter]);
  const counts = useMemo(() => Object.fromEntries(KEYS.map((k) => [k, TROLLS.filter(FILTERS[k]).length])), []);

  const change = (k) => {
    setFilter(k);
    setLimit(PAGE);
  };

  return (
    <Section id="trolls" testId="gallery-section">
      <SectionHead kicker={t.gallery.kicker} title={t.gallery.title} body={t.gallery.body} />
      <div className="mt-10 mb-8">
        <Chips options={KEYS.map((k) => [k, t.gallery.chips[k]])} value={filter} onChange={change} prefix="gallery-filter" counts={counts} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-testid="gallery-grid">
        {list.slice(0, limit).map((tr, i) => (
          <TrollCard key={tr.id} troll={tr} onOpen={setOpen} i={i} />
        ))}
      </div>
      <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <span className="text-sm text-ink-muted" data-testid="gallery-count">
          <b className="text-ink tabular-nums">{Math.min(limit, list.length)}</b> / {list.length} · {t.gallery.portraitNote}
        </span>
        {limit < list.length && (
          <button
            type="button"
            data-testid="gallery-show-more"
            onClick={() => setLimit((l) => l + PAGE)}
            className="rounded-full border-2 border-ink text-ink px-6 py-3 font-extrabold hover:bg-ink hover:text-parchment transition-colors"
          >
            {t.gallery.more}
          </button>
        )}
      </div>
      <JournalDialog troll={open} onClose={() => setOpen(null)} />
    </Section>
  );
}
